import { Vue, Component, Prop } from "vue-property-decorator";

import TileLayer from "ol/layer/Tile";
import TileWMS from "ol/source/TileWMS";

import settings from "@/settings";
import { geoServerService } from "@/services/geoServerService";

/** WFS version used for downloads; the layer `version` field is the WMS one. */
const WFS_VERSION = '2.0.0';

@Component({})
export default class TocItem extends Vue {
    @Prop({ required: true })
    layer: TileLayer<TileWMS>

    @Prop({ required: true })
    layerIndex: number

    @Prop({ required: false, default: () => [] })
    splitedLayersIds: number[];

    @Prop({ default: null, required: false })
    tab: string

    get params() {
        return this.layer.getSource()?.getParams() as geoserver.params;
    }

    get styles(): { value: string, label: string }[] {
        return settings.wmsLayers.find(f => f.layer === this.params.LAYERS && f.workspace === this.params.WORKSPACE && f.version === this.params.VERSION)?.styles ?? [];
    }


    private privateStyleSelected: { value: string, label: string } = null;
    public get styleSelected(): { value: string, label: string } {
        return this.privateStyleSelected;
    }
    public set styleSelected(v: { value: string, label: string }) {
        if (v && v.value !== this.privateStyleSelected?.value) {
            this.privateStyleSelected = v;
            this.$emit('changeStyle', { params: this.params, style: this.privateStyleSelected })
            this.downloadLegend()
        }
    }


    private privateActiveFn: string = null;
    public get activeFn(): string {
        return this.privateActiveFn;
    }
    public set activeFn(v: string) {
        if (this.privateActiveFn === v) {
            this.privateActiveFn = null;
        } else {
            this.privateActiveFn = v;
        }
    }

    public get visible(): any {
        return this.layer.getVisible();
    }
    public set visible(value: any) {
        if (this.splitedLayersIds.includes(this.layerIndex)) {
            this.handleSwipe();
        }
        this.layer.setVisible(value);
    }

    public get opacity(): any {
        return this.layer.getOpacity();
    }
    public set opacity(v: any) {
        this.layer.setOpacity(parseFloat(v));
    }

    public get zIndex(): any {
        return this.layer.getZIndex();
    }
    public set zIndex(v: any) {
        this.layer.setZIndex(parseFloat(v));
    }

    get metadata(): layer.metadata {
        const emptyContent = { title: null, description: null, downloadTypes: [] };
        if (!this.params) return emptyContent
        return settings.wmsLayers.find(f => f.workspace === this.params.WORKSPACE && f.layer === this.params.LAYERS)?.metadata ?? emptyContent;
    }

    get swipePosition(): 'left' | 'right' | null {
        const idx = this.splitedLayersIds.indexOf(this.layerIndex);
        if (idx >= 0) {
            if (idx === 0) {
                return 'left';
            }
            return 'right';
        }
        return null;
    }

    legendUrl: string = null;
    async created() {
        this.styleSelected = this.styles && this.styles.length > 0 ? this.styles[0] : null;
        this.downloadLegend();
    }

    mounted() {
        this.activeFn = this.tab ? this.tab : 'description';
        if (this.activeFn === 'split-view' && this.visible) {
            setTimeout(this.handleSwipe, 1000)
        }
    }

    downloadLegend() {
        this.legendUrl = null;
        const props = { STYLE: this.styleSelected?.value, LAYER: this.params.LAYERS };
        const basicUrl = this.layer.getSource().getLegendUrl(null, props);
        this.$nextTick(async () => {
            this.legendUrl = await geoServerService.download(basicUrl);
        })
    }

    handleSwipe() {
        this.activeFn = 'split-view';
        this.$emit('split-view');
    }

    downloading: string = null;
    async handleDownload(layerType: 'WPS' | 'WMS' | 'WFS' | 'STYLE', downloadType: string, title: string, extension: string) {
        this.downloading = `${this.params.LAYERS}.${title}.${extension}`;
        let _url: string = null
        switch (layerType) {
            case 'WPS': {
                _url = await geoServerService.downloadPluginWPS(this.params, downloadType);
                break;
            }
            case 'WMS': {
                // WMS 1.3.0 takes `crs` (not `srs`); the extent comes from the layer itself.
                const extent = this.layer.getExtent()?.map(value => Math.round(value)).join(',');
                const projection = this.layer.getSource().getProjection()?.getCode() ?? 'EPSG:3857';
                _url = await geoServerService.download(`${settings.geoServerUrl}/wms?service=WMS&version=${this.params.VERSION}&request=GetMap&layers=${this.params.WORKSPACE}%3A${this.params.LAYERS}&bbox=${extent}&width=768&height=628&crs=${projection}&styles=&format=${downloadType}`);
                break;
            }
            case 'WFS': {
                _url = await geoServerService.download(`${settings.geoServerUrl}/wfs?service=WFS&version=${WFS_VERSION}&request=GetFeature&typeName=${this.params.WORKSPACE}%3A${this.params.LAYERS}&outputFormat=${downloadType}`);
                break;
            }
            case 'STYLE': {
                _url = await geoServerService.download(`/assets/geo-styles/${this.metadata.styleZip}`);
                break;
            }
        }

        if (!!_url) {
            var a = document.createElement("a") as any;
            document.body.appendChild(a);
            a.style = "display: none; visibility: hidden;";
            a.href = _url;
            a.download = this.downloading;
            a.click();
            this.$nextTick(() => {
                document.body.removeChild(a);
            })
        }

        this.downloading = null;
    }
}